import {PERMISSION_FIELDS} from '../constants/permissionConstants'

export {PERMISSION_ID_MAP} from '../constants/permissionConstants'

export const getJurisdictionNames = (jurisdictionIds, jurisdiction = []) => {
  if (!jurisdictionIds || jurisdictionIds.length === 0) return []
  if (!jurisdiction || jurisdiction.length === 0) return jurisdictionIds.map((id) => `State ${id}`)

  const normalizedIds = new Set(jurisdictionIds.map((id) => (typeof id === 'string' ? Number.parseInt(id, 10) : id)))

  return jurisdiction
    .filter((option) => normalizedIds.has(option.STATE_ID))
    .map((option) => option.title || option.STATE_NAME)
}

export const getDataAggregationNames = (aggregationIds, dataAggregationConfig = {}) => {
  if (!aggregationIds || aggregationIds.length === 0) return []
  const dataList = dataAggregationConfig?.data || []
  if (dataList.length === 0) return aggregationIds.map((id) => `Aggregation ${id}`)

  const aggregationIdSet = new Set(aggregationIds)
  return dataList.filter((item) => aggregationIdSet.has(item.key)).map((item) => item.value)
}

const buildFilterListFromConfig = (filterConfig = {}) => {
  const filters = []
  const data = filterConfig?.data || []
  for (const category of data) {
    const categoryFilters = category.filters || []
    for (const filter of categoryFilters) {
      filters.push({
        key: filter.key,
        name: filter.name,
        categoryName: category.title,
        categoryId: category.id
      })
    }
  }
  return filters
}

export const getFilterNames = (filterKeys, filterConfig = {}) => {
  if (!filterKeys || filterKeys.length === 0) return []
  const filterList = buildFilterListFromConfig(filterConfig)
  if (filterList.length === 0) return filterKeys

  const keySet = new Set(filterKeys)
  return filterList.filter((f) => keySet.has(f.key)).map((f) => f.name)
}

export const getFiltersByCategory = (filterKeys, filterConfig = {}) => {
  if (!filterKeys || filterKeys.length === 0) return {}
  const filterList = buildFilterListFromConfig(filterConfig)
  if (filterList.length === 0) return {}

  const keySet = new Set(filterKeys)
  const categorizedFilters = {}
  const selectedFilters = filterList.filter((f) => keySet.has(f.key))

  for (const filter of selectedFilters) {
    const categoryName = filter.categoryName || 'Other'
    if (!categorizedFilters[categoryName]) {
      categorizedFilters[categoryName] = []
    }
    categorizedFilters[categoryName].push(filter.name)
  }

  return categorizedFilters
}

// Get the category name for a given filter key
export const getFilterCategoryByFilterKey = (filterKey, filterConfig = {}) => {
  const filterList = buildFilterListFromConfig(filterConfig)
  const filter = filterList.find((f) => f.key === filterKey)
  return filter?.categoryName || null
}

export const getPointOfInterestNames = (poiIds, pointOfInterestsData = []) => {
  if (!poiIds || poiIds.length === 0) return []
  if (!pointOfInterestsData || pointOfInterestsData.length === 0) return poiIds.map((id) => `POI ${id}`)

  return pointOfInterestsData
    .filter((option) => poiIds.includes(option.POI_ID))
    .map((option) => option.title || option.label)
}

// Helper to flatten nested use case structure
const flattenUseCases = (useCases = []) => {
  const allUseCases = []
  const processUseCase = (useCase, domainName) => {
    allUseCases.push({id: useCase.id, name: useCase.name, category: domainName})
  }
  const processSubTopic = (subTopic, domainName) => {
    const cases = subTopic.useCases || []
    for (const uc of cases) {
      processUseCase(uc, domainName)
    }
  }
  const processTopic = (topic, domainName) => {
    const subTopics = topic.subTopics || []
    for (const st of subTopics) {
      processSubTopic(st, domainName)
    }
  }
  for (const domain of useCases) {
    const topics = domain.topics || []
    for (const topic of topics) {
      processTopic(topic, domain.name)
    }
  }
  return allUseCases
}

export const getUseCaseNames = (useCaseIds, useCases = []) => {
  if (!useCaseIds || useCaseIds.length === 0) return []
  if (!useCases || useCases.length === 0) return useCaseIds.map((id) => `UseCase ${id}`)

  const allUseCases = flattenUseCases(useCases)
  const normalizedIds = new Set(useCaseIds.map(String))
  return allUseCases.filter((uc) => normalizedIds.has(String(uc.id))).map((uc) => uc.name)
}

export const getUseCasesByCategory = (useCaseIds, useCases = []) => {
  if (!useCaseIds || useCaseIds.length === 0) return {}
  if (!useCases || useCases.length === 0) return {}

  const allUseCases = flattenUseCases(useCases)
  const categorizedUseCases = {}
  const normalizedIds = new Set(useCaseIds.map(String))

  const filteredUseCases = allUseCases.filter((uc) => normalizedIds.has(String(uc.id)))
  for (const uc of filteredUseCases) {
    if (!categorizedUseCases[uc.category]) {
      categorizedUseCases[uc.category] = []
    }
    categorizedUseCases[uc.category].push(uc.name)
  }

  return categorizedUseCases
}

// Get the category name for a given use case ID
export const getUseCaseCategoryById = (useCaseId, useCases = []) => {
  const allUseCases = flattenUseCases(useCases)
  const normalizedId = String(useCaseId)
  const useCase = allUseCases.find((uc) => String(uc.id) === normalizedId)
  return useCase?.category || null
}

export const getBenefitNames = (benefitIds, benefits = []) => {
  if (!benefitIds || benefitIds.length === 0) return []
  if (!benefits || benefits.length === 0) return benefitIds.map((id) => `Benefit ${id}`)

  return benefits.filter((option) => benefitIds.includes(option.id)).map((option) => option.title)
}

export const getFlagValue = (flagArray) => {
  if (!flagArray || flagArray.length === 0) return false
  return flagArray[0] === 1
}

export const getDisplayName = (fieldName, value, providerData = {}) => {
  const {jurisdiction, dataAggregationConfig, filterConfig, pointOfInterestsData, useCases} = providerData

  switch (fieldName) {
    case PERMISSION_FIELDS.JURISDICTION:
      return getJurisdictionNames([value], jurisdiction)[0] || value
    case PERMISSION_FIELDS.DATA_AGGREGATION:
      return getDataAggregationNames([value], dataAggregationConfig)[0] || value
    case PERMISSION_FIELDS.FILTERS:
      return getFilterNames([value], filterConfig)[0] || value
    case PERMISSION_FIELDS.POINT_OF_INTEREST:
      return getPointOfInterestNames([value], pointOfInterestsData)[0] || value
    case PERMISSION_FIELDS.USECASE:
      return getUseCaseNames([value], useCases)[0] || value
    default:
      return value
  }
}

export const getOriginalIds = (fieldName, displayName, providerData = {}) => {
  const {jurisdiction, dataAggregationConfig, filterConfig, pointOfInterestsData, useCases} = providerData

  switch (fieldName) {
    case PERMISSION_FIELDS.JURISDICTION:
      return (
        jurisdiction?.filter((s) => (s.title || s.STATE_NAME) === displayName).map((s) => s.STATE_ID) || [displayName]
      )
    case PERMISSION_FIELDS.DATA_AGGREGATION: {
      const dataList = dataAggregationConfig?.data || []
      return dataList?.filter((d) => d.value === displayName).map((d) => d.key) || [displayName]
    }
    case PERMISSION_FIELDS.FILTERS: {
      const filterList = buildFilterListFromConfig(filterConfig)
      const matchingFilters = filterList?.filter((f) => f.name === displayName) || []
      if (matchingFilters.length === 0) return [displayName]
      return matchingFilters.map((f) => f.key)
    }
    case PERMISSION_FIELDS.POINT_OF_INTEREST:
      return (
        pointOfInterestsData?.filter((p) => (p.title || p.label) === displayName).map((p) => p.POI_ID) || [displayName]
      )
    case PERMISSION_FIELDS.USECASE: {
      const allUseCases = flattenUseCases(useCases)
      return allUseCases?.filter((uc) => uc.name === displayName).map((uc) => uc.id) || [displayName]
    }
    default:
      return [displayName]
  }
}
